import React, { useState } from "react";
import { Printer, Mail, Check, Send, Download } from "lucide-react";
import { Modal } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { sendDoc } from "../lib/sendDoc.js";
import { buildInvoicePdfBase64, invoicePdfFilename, downloadInvoicePdf } from "./invoicePdf.js";
import { fmtMoney, fmtDate, todayStr } from "../lib/format.js";
import { fmtTime, rentalDays, lateInfo } from "../lib/rental-time.js";

// v10 showed a second banner on reserved rentals opening "This is an invoice —",
// directly under a heading reading QUOTE. Removed: a document cannot usefully
// tell the reader it is both.
//
// Its one piece of substance — the card-on-file notice — moved into the quote
// disclaimer below, so it still reaches the customer without a contradictory
// heading around it. Wording is Matt's: "may be charged" rather than v10's
// "will be charged", and it states what the charge is for.

export const QUOTE_TEXT =
  "This quote is an estimate of cost and is subject to change. Your final invoice will reflect " +
  "any additional charges incurred during the rental. Upon confirmation, your card on file may " +
  "be charged applicable fees to secure your rental.";

/**
 * A reserved or on-rent rental produces a QUOTE; only a returned one produces
 * the final INVOICE. That flip is the whole quote→invoice lifecycle, and it is
 * driven by status rather than by a separate document record.
 */
const docKind = (rental) => ({
  pending: rental.status === "reserved",
  isFinal: rental.status === "returned",
});

export function InvoiceBody({ rental, settings }) {
  const eq = rental.equipment ?? {};
  const c = rental.customers ?? {};
  const s = settings ?? {};
  const days = rentalDays(rental);
  const charge = rental.rental_charge ?? 0;
  const { pending, isFinal } = docKind(rental);
  const li = lateInfo(rental);

  return (
    <>
      {!isFinal && (
        <div className="inv-hold" style={{ borderColor: "var(--steel)", background: "#f0efec", color: "#3a4149" }}>
          {QUOTE_TEXT}
        </div>
      )}

      <div className="inv-head">
        <div>
          <div className="wordmark">HAL<span>CO</span></div>
          <div style={{ fontWeight: 600 }}>{s.company}</div>
          <div style={{ color: "var(--muted)" }}>
            {s.addr} · {s.phone}<br />{s.email}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "var(--disp)", fontSize: 30, fontWeight: 700, letterSpacing: ".08em" }}>
            {isFinal ? "INVOICE" : "QUOTE"}
          </div>
          <div><b>{rental.invoice_no}</b></div>
          <div style={{ color: "var(--muted)" }}>Issued {fmtDate(todayStr())}</div>
          <div style={{ marginTop: 10 }}>
            <span
              className={"stamp" + (rental.paid ? "" : " due")}
              style={!rental.paid && !isFinal && !pending ? { borderColor: "var(--steel)", color: "var(--steel)" } : {}}
            >
              {rental.paid ? "Paid" : isFinal ? "Balance due" : pending ? "Reservation held" : "Estimate"}
            </span>
          </div>
        </div>
      </div>

      <div className="inv-grid">
        <div>
          <h4>Bill to</h4>
          <div style={{ fontWeight: 700 }}>{c.name || "—"}</div>
          {c.business && <div>{c.business}</div>}
          {(c.billing_address || c.home_address) && <div>{c.billing_address || c.home_address}</div>}
          {c.phone && <div>{c.phone}</div>}
          {c.email && <div>{c.email}</div>}
        </div>
        <div>
          <h4>Rental period</h4>
          <div>
            {fmtDate(rental.start_date)} {fmtTime(rental.start_time)} → {fmtDate(rental.end_date)}{" "}
            {fmtTime(rental.end_time)} ({days} day{days === 1 ? "" : "s"})
          </div>
          {isFinal && rental.returned_date && (
            <div style={{ marginTop: 4 }}>
              Returned: {fmtDate(rental.returned_date)} {fmtTime(rental.returned_time)}
              {li ? <b style={{ color: "var(--red)" }}> — {li.label} late</b> : " — on time"}
            </div>
          )}
          {Number(rental.deposit) > 0 && (
            <div style={{ marginTop: 6 }}>
              Security deposit held: <b>{fmtMoney(rental.deposit)}</b>
            </div>
          )}
        </div>
      </div>

      <table>
        <thead>
          <tr><th>Description</th><th style={{ textAlign: "right" }}>Amount</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Equipment rental — {eq.name || "Equipment"}
              <br />
              <span style={{ color: "var(--muted)", fontSize: 12 }}>
                {days} day{days === 1 ? "" : "s"} · rates {fmtMoney(eq.day_rate)}/day,{" "}
                {fmtMoney(eq.week_rate)}/wk, {fmtMoney(eq.month_rate)}/mo
              </span>
            </td>
            <td style={{ textAlign: "right" }}>{fmtMoney(charge)}</td>
          </tr>
          {Number(rental.delivery_fee) > 0 && (
            <tr><td>Delivery &amp; pickup</td><td style={{ textAlign: "right" }}>{fmtMoney(rental.delivery_fee)}</td></tr>
          )}
          {Number(rental.damage_waiver) > 0 && (
            <tr><td>Damage waiver</td><td style={{ textAlign: "right" }}>{fmtMoney(rental.damage_waiver)}</td></tr>
          )}
          {Number(rental.late_charge) > 0 && (
            <tr>
              <td>
                Late return fee
                {li && <span style={{ color: "var(--muted)", fontSize: 12 }}> — {li.label} past due-back time</span>}
              </td>
              <td style={{ textAlign: "right" }}>{fmtMoney(rental.late_charge)}</td>
            </tr>
          )}
          {Number(rental.fuel_charge) > 0 && (
            <tr><td>Fuel charge</td><td style={{ textAlign: "right" }}>{fmtMoney(rental.fuel_charge)}</td></tr>
          )}
          {Number(rental.damage_charge) > 0 && (
            <tr><td>Damage charge</td><td style={{ textAlign: "right" }}>{fmtMoney(rental.damage_charge)}</td></tr>
          )}
          {Number(rental.other_fee) > 0 && (
            <tr>
              <td>{rental.other_fee_note || "Additional fee"}</td>
              <td style={{ textAlign: "right" }}>{fmtMoney(rental.other_fee)}</td>
            </tr>
          )}
          <tr className="tot">
            <td style={{ textAlign: "right" }}>
              {isFinal ? "Total" : "Estimated total"} {rental.paid ? "paid" : "due"}
            </td>
            <td style={{ textAlign: "right" }}>{fmtMoney(rental.total)}</td>
          </tr>
        </tbody>
      </table>

      {rental.notes && (
        <div style={{ marginBottom: 10 }}>
          <h4>Notes</h4>
          {rental.notes}
        </div>
      )}

      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10, color: "var(--muted)", fontSize: 12 }}>
        Thank you for renting with {s.company}. {s.delivery_note}. Questions? Call {s.phone}.
      </div>
    </>
  );
}

/**
 * Short covering note that accompanies the attached PDF.
 *
 * The itemised breakdown lives in the attachment, so the body stays readable.
 * A compact summary line is still included: some mail clients collapse
 * attachments, and a customer should be able to see what this is and what it
 * costs without opening anything.
 *
 * Wording is Matt's, adapted per document type — the reservation-hold sentence
 * only makes sense on a quote, not on an invoice for a rental already returned.
 */
export function buildCoveringNote(rental, settings) {
  const eq = rental.equipment ?? {};
  const c = rental.customers ?? {};
  const s = settings ?? {};
  const { isFinal } = docKind(rental);
  const days = rentalDays(rental);

  const summary = [
    `${eq.name ?? "Equipment"} — ${fmtDate(rental.start_date)} to ${fmtDate(rental.end_date)} (${days} day${days === 1 ? "" : "s"})`,
    `${isFinal ? "Total due" : "Estimated total due"}: ${fmtMoney(rental.total)}`,
  ];

  const lines = [
    `Hi ${(c.name ?? "").split(" ")[0] || "there"},`,
    "",
    isFinal
      ? "Attached is your final invoice for your recent rental."
      : "Attached is your quote for your requested rental.",
    "",
    ...summary,
    "",
    ...(isFinal
      ? []
      : ["This reservation will be held in our system for 3 hours until a deposit is received.", ""]),
    "Please do not hesitate to reach out if you have any questions.",
    "",
    "Thank you for supporting our small business!",
    "",
    "- Matt",
    `Owner, ${s.company ?? "HALCO Equipment Rentals"}`,
    s.phone ?? "",
  ].filter((l) => l !== null);

  return {
    subject: `${s.company} — ${isFinal ? "Invoice" : "Quote"} ${rental.invoice_no ?? ""}`,
    body: lines.join("\n").replace(/\n{3,}/g, "\n\n"),
  };
}

/**
 * Full itemised plain-text version, same lines and order as v10.
 *
 * Still used by "Copy text", which exists for pasting into Gmail by hand — no
 * attachment there, so the breakdown has to be in the body.
 */
export function buildInvoiceEmail(rental, settings) {
  const eq = rental.equipment ?? {};
  const c = rental.customers ?? {};
  const s = settings ?? {};
  if (!c.email) return null;

  const days = rentalDays(rental);
  const { pending, isFinal } = docKind(rental);
  const docWord = isFinal ? "Invoice" : "Quote";
  const li = lateInfo(rental);
  const money = (v) => fmtMoney(v);

  const lines = [
    `${s.company} — ${docWord} ${rental.invoice_no ?? ""}`,
    "",
    ...(!isFinal ? [QUOTE_TEXT, ""] : []),
    `Bill to: ${c.name ?? ""}${c.business ? ` (${c.business})` : ""}`,
    `Equipment: ${eq.name ?? "Equipment"}`,
    `Rental period: ${fmtDate(rental.start_date)} ${fmtTime(rental.start_time)} to ${fmtDate(rental.end_date)} ${fmtTime(rental.end_time)} (${days} day${days === 1 ? "" : "s"})`,
    ...(isFinal && rental.returned_date
      ? [`Returned: ${fmtDate(rental.returned_date)} ${fmtTime(rental.returned_time)}${li ? ` (${li.label} late)` : " (on time)"}`]
      : []),
    "",
    `Equipment rental: ${money(rental.rental_charge)}`,
    ...(Number(rental.delivery_fee) > 0 ? [`Delivery & pickup: ${money(rental.delivery_fee)}`] : []),
    ...(Number(rental.damage_waiver) > 0 ? [`Damage waiver: ${money(rental.damage_waiver)}`] : []),
    ...(Number(rental.late_charge) > 0 ? [`Late return fee${li ? ` (${li.label})` : ""}: ${money(rental.late_charge)}`] : []),
    ...(Number(rental.fuel_charge) > 0 ? [`Fuel charge: ${money(rental.fuel_charge)}`] : []),
    ...(Number(rental.damage_charge) > 0 ? [`Damage charge: ${money(rental.damage_charge)}`] : []),
    ...(Number(rental.other_fee) > 0 ? [`${rental.other_fee_note || "Additional fee"}: ${money(rental.other_fee)}`] : []),
    ...(Number(rental.deposit) > 0 ? [`Security deposit (held): ${money(rental.deposit)}`] : []),
    `${isFinal ? "Total " : "Estimated total "}${rental.paid ? "paid" : "due"}: ${money(rental.total)}`,
    "",
    ...(rental.notes ? [`Notes: ${rental.notes}`, ""] : []),
    `Questions? Call ${s.phone} or reply to this email.`,
    `Thank you for renting with ${s.company}.`,
  ];

  const subject = `${s.company} — ${docWord} ${rental.invoice_no ?? ""}${pending ? " (reservation held, pending confirmation)" : ""}`;
  const body = lines.join("\n");

  return {
    subject,
    body,
    to: c.email,
    cc: s.email,
    href: `mailto:${encodeURIComponent(c.email)}?cc=${encodeURIComponent(s.email ?? "")}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    text: `To: ${c.email}\nCC: ${s.email ?? ""}\nSubject: ${subject}\n\n${body}`,
  };
}

export default function InvoiceModal({ rental, settings, onClose }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [sendErr, setSendErr] = useState("");
  const { notify } = useToast();
  const mail = buildInvoiceEmail(rental, settings);
  const { isFinal } = docKind(rental);

  /**
   * Sending is a button rather than something that fires automatically on
   * check-in or on accepting a booking, which is a deliberate departure from
   * the plan.
   *
   * The document below is the money. Late fees, fuel, and damage are entered by
   * hand at check-in, and an automatic send would post the invoice before
   * anyone had looked at those numbers. One click beats copy-and-paste into
   * Gmail either way; it just keeps a person between the total and the customer.
   */
  async function send() {
    if (!mail) {
      setSendErr("This customer has no email address on file — add one under Customers.");
      return;
    }
    setSendErr("");
    setSending(true);
    try {
      const note = buildCoveringNote(rental, settings);
      const res = await sendDoc({
        rentalId: rental.id,
        kind: isFinal ? "invoice" : "quote",
        subject: note.subject,
        body: note.body,
        attachment: {
          filename: invoicePdfFilename(rental),
          content: buildInvoicePdfBase64(rental, settings),
        },
      });
      setSent(res);
      notify(`${isFinal ? "Invoice" : "Quote"} emailed to ${res.to}.`);
    } catch (e) {
      setSendErr(e.message);
    } finally {
      setSending(false);
    }
  }

  async function copyEmail() {
    if (!mail) {
      notify("This customer has no email on file — add one under Customers.");
      return;
    }
    try {
      await navigator.clipboard.writeText(mail.text);
      setCopied(true);
      notify("Invoice email copied — paste into Gmail or Outlook. Customer is in To, you're CC'd.");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      notify("Your browser blocked copying. Use “Open in email app” instead.");
    }
  }

  return (
    <Modal
      title={isFinal ? "Invoice" : "Quote"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn dark" onClick={send} disabled={sending || !!sent || !mail}>
            <Send size={15} />{" "}
            {sending ? "Sending…" : sent ? "Sent" : `Email ${isFinal ? "invoice" : "quote"}`}
          </button>
          <button className="btn ghost" onClick={copyEmail}>
            {copied ? <Check size={15} /> : <Mail size={15} />} {copied ? "Copied" : "Copy text"}
          </button>
          {mail && (
            <a className="btn ghost" href={mail.href}>
              <Mail size={15} /> Open in email app
            </a>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose}>Close</button>
          <button className="btn ghost" onClick={() => downloadInvoicePdf(rental, settings)}>
            <Download size={15} /> PDF
          </button>
          <button className="btn primary" onClick={() => window.print()}>
            <Printer size={15} /> Print
          </button>
        </>
      }
    >
      {sendErr && <div className="notice red noprint">{sendErr}</div>}
      {sent && (
        <div
          className="notice noprint"
          style={{ background: "#e7f0ea", borderColor: "#bcd6c4", color: "#1e5631" }}
        >
          Sent to <b>{sent.to}</b>
          {sent.cc ? <> · copied to {sent.cc}</> : null}.
        </div>
      )}

      {/* print-zone is what the CSS print rules isolate, so only this prints. */}
      <div className="inv print-zone">
        <InvoiceBody rental={rental} settings={settings} />
      </div>
    </Modal>
  );
}
